// WikiTraining.jsx — admin training surface: knowledge gaps, canonical answers,
// tone & rules, per-department knowledge-source toggles, retrain.
import React, { useEffect, useState, useCallback } from 'react';
import {
    HelpCircle, BookCheck, SlidersHorizontal, Database,
} from 'lucide-react';
import { toast } from 'sonner';
import { useWikiApi } from '../../../hooks/useWiki';
import { DEPT_BY_ID, deptColors } from '../../../lib/wikiConstants';
import WikiCiteChip from '../WikiCiteChip';
import { WikiAiAvatar } from '../WikiCiteChip';

export default function WikiTraining() {
    const api = useWikiApi();
    const [data, setData] = useState(null);
    const [tone, setTone] = useState('');
    const [teach, setTeach] = useState(null);   // {q, dept} being taught
    const [teachA, setTeachA] = useState('');

    const load = useCallback(() => {
        api.getTraining().then(d => { setData(d); setTone(d.training?.tone_rules || ''); }).catch(() => setData(null));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="p-10 text-stone-400 text-[13px]">Loading training…</div>;
    const t = data.training || {};
    const gaps = data.gaps || [];
    const canonical = t.canonical || [];
    const toggles = t.source_toggles || {};
    const pageCounts = data.page_counts || {};

    const saveTone = async () => { try { await api.setTraining({ tone_rules: tone }); toast.success('Tone & rules saved'); } catch { toast.error('Could not save'); } };
    const toggleSource = async (deptId) => {
        const next = { ...toggles, [deptId]: !(toggles[deptId] !== false) };
        setData(d => ({ ...d, training: { ...d.training, source_toggles: next } }));
        try { await api.setTraining({ source_toggles: { [deptId]: next[deptId] } }); } catch { toast.error('Could not save'); load(); }
    };
    const saveTaught = async () => {
        try {
            const canon = canonical.map(c => ({ canon_id: c.canon_id, dept: c.dept, q: c.q, a: c.a, source_page_id: c.source_page_id }));
            canon.push({ dept: teach.dept, q: teach.q, a: teachA, source_page_id: null });
            await api.setTraining({ canonical: canon });
            toast.success('Answer taught'); setTeach(null); setTeachA(''); load();
        } catch { toast.error('Could not save'); }
    };
    const retrain = async () => { try { const r = await api.retrain(); toast.success(r.message || 'Retrained'); } catch { toast.error('Could not retrain'); } };

    return (
        <div className="p-5 grid grid-cols-2 gap-4 max-w-[1000px]">
            <div className="space-y-4">
                <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft">
                    <div className="px-4 h-11 flex items-center gap-2 border-b border-stone-200"><HelpCircle className="w-4 h-4 text-amber-500" /><span className="text-[13px] wiki-display font-bold text-stone-900">Knowledge gaps</span><span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{gaps.length}</span></div>
                    <div className="divide-y divide-stone-200">
                        {gaps.map((g, i) => (
                            <div key={i} className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-stone-900">{g.q}</div><div className="text-[11px] text-stone-500 mt-0.5">asked {g.n}× · {DEPT_BY_ID[g.dept]?.name || g.dept}</div></div>
                                    <button onClick={() => { setTeach(g); setTeachA(''); }} className="h-8 px-3 rounded-lg wiki-grad text-white text-[11px] font-semibold shrink-0">Teach answer</button>
                                </div>
                                {teach && teach.q === g.q && (
                                    <div className="mt-2 space-y-2">
                                        <textarea value={teachA} onChange={e => setTeachA(e.target.value)} placeholder="Write the canonical answer…" className="w-full h-20 rounded-lg border border-stone-200 p-2 text-[13px] resize-none focus:outline-none focus:border-stone-400" />
                                        <div className="flex gap-2"><button onClick={saveTaught} disabled={!teachA.trim()} className="h-8 px-3 rounded-lg wiki-grad text-white text-[12px] font-semibold disabled:opacity-50">Save</button><button onClick={() => setTeach(null)} className="h-8 px-3 rounded-lg border border-stone-200 text-[12px] font-semibold">Cancel</button></div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {gaps.length === 0 && <div className="px-4 py-6 text-center text-[12px] text-stone-400">No knowledge gaps yet.</div>}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft">
                    <div className="px-4 h-11 flex items-center gap-2 border-b border-stone-200"><BookCheck className="w-4 h-4 text-emerald-600" /><span className="text-[13px] wiki-display font-bold text-stone-900">Canonical answers</span></div>
                    <div className="divide-y divide-stone-200">
                        {canonical.map((c, i) => (
                            <div key={i} className="px-4 py-3"><div className="text-[13px] font-medium text-stone-900">{c.q}</div><div className="text-[12px] text-stone-600 mt-0.5">{c.a}</div>{c.source_page_id && <div className="mt-1.5"><WikiCiteChip cite={{ page_id: c.source_page_id, name: 'Source', dept: c.dept }} onOpenDoc={() => { }} /></div>}</div>
                        ))}
                        {canonical.length === 0 && <div className="px-4 py-6 text-center text-[12px] text-stone-400">No canonical answers yet — teach one from a knowledge gap or an interaction.</div>}
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft p-4">
                    <div className="flex items-center gap-2 mb-3"><SlidersHorizontal className="w-4 h-4 text-stone-900" /><span className="text-[13px] wiki-display font-bold text-stone-900">Tone &amp; rules</span><button onClick={saveTone} className="ml-auto text-[11px] font-semibold text-stone-900 hover:text-stone-900">Save</button></div>
                    <textarea value={tone} onChange={e => setTone(e.target.value)} className="w-full h-28 rounded-lg border border-stone-200 p-3 text-[13px] text-stone-600 resize-none focus:outline-none focus:border-stone-400 leading-relaxed" />
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {(t.rule_flags || []).map(f => <span key={f} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-800 border border-stone-300">{f}</span>)}
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft p-4">
                    <div className="flex items-center gap-2 mb-3"><Database className="w-4 h-4 text-stone-900" /><span className="text-[13px] wiki-display font-bold text-stone-900">Knowledge sources</span></div>
                    <div className="space-y-2">
                        {(data.departments || []).map(d => {
                            const on = toggles[d.id] !== false;
                            const c = deptColors(d.id);
                            const Ico = DEPT_BY_ID[d.id]?.icon;
                            return (
                                <div key={d.id} className="flex items-center gap-2.5">
                                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${c.tile}`}>{Ico && <Ico className="w-3.5 h-3.5" />}</div>
                                    <span className="text-[12px] font-medium text-stone-900 flex-1">{d.name} wiki</span>
                                    <span className="text-[11px] text-stone-500">{pageCounts[d.id] ?? 0} pages</span>
                                    <button onClick={() => toggleSource(d.id)} className={`w-9 h-5 rounded-full relative transition ${on ? 'bg-emerald-500' : 'bg-stone-300'}`}>
                                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? 'right-0.5' : 'left-0.5'}`} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="wiki-ai-border rounded-xl bg-white p-4">
                    <div className="flex items-center gap-2"><WikiAiAvatar size={28} /><div><div className="text-[13px] wiki-display font-bold wiki-grad-text">Retrain agents</div><div className="text-[11px] text-stone-500">Apply edits — agents use live retrieval, so changes are instant.</div></div><button onClick={retrain} className="ml-auto h-9 px-3.5 rounded-lg wiki-grad text-white text-[12px] font-semibold">Retrain</button></div>
                </div>
            </div>
        </div>
    );
}
